import React, { useCallback, useMemo } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import Pane from 'components/Common/Pane'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import Heading from 'LuxKit/Typography/Heading'
import BodyText from 'LuxKit/Typography/BodyText'
import TextInput from 'components/Common/Form/Input/TextInput'
import SwitchButton from 'LuxKit/Switch/SwitchButton'
import TextButton from 'LuxKit/Button/TextButton'
import RadioButton from 'LuxKit/Radio/RadioButton'
import { useAppSelector, useAppDispatch } from 'hooks/reduxHooks'
import { DEV_OVERRIDE_AUTH_ACCOUNT, DEV_OVERRIDE_LOYALTY_STATE, SYNC_USER_DETAILS } from 'actions/actionConstants'
import { getTierOrder } from 'loyalty/lib/tierUtils'
import { addMonths as addMonthsToDate } from 'lib/datetime/dateUtils'
import * as isomorphicSessionStorage from 'lib/storage/isomorphicSessionStorage'

const SESSION_KEY = 'DevPanel:customerProfileOverrides'

interface StoredOverrides {
  tier?: App.LoyaltyTier
  points?: number
  statusCredits?: number
  numberOfNights?: number
  societeJoinDate?: string
  hasAccount?: boolean
  luxPlusActive?: boolean
  luxPlusSavings?: number
  luxPlusJoinDate?: string
  credits?: number
  givenName?: string
  surname?: string
}

function loadOverrides(): StoredOverrides {
  return isomorphicSessionStorage.get(SESSION_KEY) ?? {}
}

function saveOverrides(overrides: StoredOverrides) {
  isomorphicSessionStorage.set(SESSION_KEY, overrides)
}

function persistOverride(patch: StoredOverrides) {
  saveOverrides({ ...loadOverrides(), ...patch })
}

const RadioLabel = styled.label`
  display: flex;
  align-items: center;
  gap: ${rem(6)};
  cursor: pointer;
  text-transform: capitalize;
  white-space: nowrap;
`

const TierRow = styled.div`
  display: flex;
  gap: ${rem(16)};
`

const Card = styled.div`
  border: 1px solid ${props => props.theme.palette.neutral.default.five};
  border-radius: ${rem(8)};
  padding: ${rem(16)};
`

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`

const FieldRow = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(${rem(100)}, 1fr));
  gap: ${rem(12)};
`

function toDateInputValue(isoDate: string | undefined): string {
  if (!isoDate) return ''
  return isoDate.split('T')[0]
}

function todayDateString(): string {
  return new Date().toISOString().split('T')[0]
}

function isValidPastDate(value: string): boolean {
  if (!value) return false
  const date = new Date(value + 'T00:00:00')
  if (isNaN(date.getTime())) return false
  const today = new Date()
  today.setHours(23, 59, 59, 999)
  return date <= today
}

function addMonths(dateStr: string, months: number): string {
  const result = addMonthsToDate(new Date(dateStr + 'T00:00:00'), months)
  return result.toISOString().split('T')[0]
}

function formatDisplayDate(dateStr: string): string {
  const d = new Date(dateStr + 'T00:00:00')
  if (isNaN(d.getTime())) return ''
  return d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })
}

function commitOnEnter(e: React.KeyboardEvent<HTMLInputElement>) {
  if (e.key === 'Enter') {
    e.currentTarget.blur()
  }
}

function CustomerProfileTab() {
  const dispatch = useAppDispatch()

  const loyaltyAccount = useAppSelector(state => state.loyalty.account.data)
  const authAccount = useAppSelector(state => state.auth.account)
  const currentRegion = useAppSelector(state => state.geo.currentRegionCode)
  const currentCurrency = useAppSelector(state => state.geo.currentCurrency)

  const hasAccount = !!loyaltyAccount
  const currentTier = loyaltyAccount?.tier ?? 'bronze'
  const currentPoints = loyaltyAccount?.points ?? 0
  const currentStatusCredits = loyaltyAccount?.statusCredits ?? 0
  const currentNights = loyaltyAccount?.numberOfNights ?? 0
  const currentStatusReviewDate = loyaltyAccount?.statusReviewDate ?? ''
  const currentMembershipStartDate = currentStatusReviewDate
    ? addMonths(toDateInputValue(currentStatusReviewDate), -12)
    : ''
  const currentCredits = authAccount.balance ?? 0
  const isLuxPlusActive = authAccount.subscriberTier === 'base' || authAccount.subscriberTier === 'free_preview'
  const currentGivenName = authAccount.givenName ?? ''
  const currentSurname = authAccount.surname ?? ''
  const currentLuxPlusJoinDate = authAccount.luxPlus?.member?.subscription?.item?.memberSince ?? ''
  const currentLuxPlusSavings = useMemo(() => {
    const benefits = authAccount.luxPlus?.member?.subscription?.item?.redeemedBenefits
    const devSavings = benefits?.find(b => b.id === 'dev-override-savings')
    return devSavings?.adjustment ?? 0
  }, [authAccount.luxPlus?.member?.subscription?.item?.redeemedBenefits])

  // --- Individual field handlers (fire on blur) ---

  const handleGivenNameBlur = useCallback((e: React.FocusEvent<HTMLInputElement>) => {
    const givenName = e.currentTarget.value
    const surname = loadOverrides().surname ?? currentSurname
    dispatch({
      type: SYNC_USER_DETAILS,
      userDetails: { givenName, surname, fullName: `${givenName} ${surname}`.trim() },
    })
    persistOverride({ givenName })
  }, [dispatch, currentSurname])

  const handleSurnameBlur = useCallback((e: React.FocusEvent<HTMLInputElement>) => {
    const surname = e.currentTarget.value
    const givenName = loadOverrides().givenName ?? currentGivenName
    dispatch({
      type: SYNC_USER_DETAILS,
      userDetails: { givenName, surname, fullName: `${givenName} ${surname}`.trim() },
    })
    persistOverride({ surname })
  }, [dispatch, currentGivenName])

  const handleCreditsBlur = useCallback((e: React.FocusEvent<HTMLInputElement>) => {
    const credits = parseFloat(e.currentTarget.value)
    if (!isNaN(credits)) {
      dispatch({ type: DEV_OVERRIDE_AUTH_ACCOUNT, overrides: { balance: credits, currencyCode: currentCurrency } })
      persistOverride({ credits })
    }
  }, [dispatch, currentCurrency])

  const handleTierChange = useCallback((tier: App.LoyaltyTier) => {
    dispatch({ type: DEV_OVERRIDE_LOYALTY_STATE, overrides: { tier, hasAccount: true, region: currentRegion } })
    persistOverride({ tier })
  }, [dispatch, currentRegion])

  const handlePointsBlur = useCallback((e: React.FocusEvent<HTMLInputElement>) => {
    const points = parseInt(e.currentTarget.value, 10)
    if (!isNaN(points)) {
      dispatch({ type: DEV_OVERRIDE_LOYALTY_STATE, overrides: { points, hasAccount: true, region: currentRegion } })
      persistOverride({ points })
    }
  }, [dispatch, currentRegion])

  const handleStatusCreditsBlur = useCallback((e: React.FocusEvent<HTMLInputElement>) => {
    const statusCredits = parseInt(e.currentTarget.value, 10)
    if (!isNaN(statusCredits)) {
      dispatch({ type: DEV_OVERRIDE_LOYALTY_STATE, overrides: { statusCredits, hasAccount: true, region: currentRegion } })
      persistOverride({ statusCredits })
    }
  }, [dispatch, currentRegion])

  const handleNightsBlur = useCallback((e: React.FocusEvent<HTMLInputElement>) => {
    const numberOfNights = parseInt(e.currentTarget.value, 10)
    if (!isNaN(numberOfNights)) {
      dispatch({ type: DEV_OVERRIDE_LOYALTY_STATE, overrides: { numberOfNights, hasAccount: true, region: currentRegion } })
      persistOverride({ numberOfNights })
    }
  }, [dispatch, currentRegion])

  const handleMembershipStartDateBlur = useCallback((e: React.FocusEvent<HTMLInputElement>) => {
    const startDate = e.currentTarget.value
    if (!isValidPastDate(startDate)) return
    const reviewDate = addMonths(startDate, 12)
    dispatch({ type: DEV_OVERRIDE_LOYALTY_STATE, overrides: { statusReviewDate: reviewDate, hasAccount: true, region: currentRegion } })
    persistOverride({ societeJoinDate: startDate })
  }, [dispatch, currentRegion])

  const handleLuxPlusSavingsBlur = useCallback((e: React.FocusEvent<HTMLInputElement>) => {
    const savings = parseFloat(e.currentTarget.value)
    if (!isNaN(savings) && savings > 0) {
      dispatch({ type: DEV_OVERRIDE_AUTH_ACCOUNT, overrides: { luxPlusSavings: savings } })
      persistOverride({ luxPlusSavings: savings })
    }
  }, [dispatch])

  const handleLuxPlusJoinDateBlur = useCallback((e: React.FocusEvent<HTMLInputElement>) => {
    const joinDate = e.currentTarget.value
    if (!isValidPastDate(joinDate)) return
    dispatch({ type: DEV_OVERRIDE_AUTH_ACCOUNT, overrides: { luxPlusJoinDate: joinDate } })
    persistOverride({ luxPlusJoinDate: joinDate })
  }, [dispatch])

  // --- Toggle handlers (preserve stored values) ---

  const handleSocieteToggle = useCallback(() => {
    if (hasAccount) {
      dispatch({ type: DEV_OVERRIDE_LOYALTY_STATE, overrides: { hasAccount: false } })
      persistOverride({ hasAccount: false })
    } else {
      const stored = loadOverrides()
      dispatch({
        type: DEV_OVERRIDE_LOYALTY_STATE,
        overrides: {
          hasAccount: true,
          region: currentRegion,
          tier: stored.tier ?? 'bronze',
          ...(stored.points !== undefined && { points: stored.points }),
          ...(stored.statusCredits !== undefined && { statusCredits: stored.statusCredits }),
          ...(stored.numberOfNights !== undefined && { numberOfNights: stored.numberOfNights }),
          ...(stored.societeJoinDate !== undefined && { statusReviewDate: addMonths(stored.societeJoinDate, 12) }),
        },
      })
      persistOverride({ hasAccount: true, tier: stored.tier ?? 'bronze' })
    }
  }, [dispatch, hasAccount, currentRegion])

  const handleLuxPlusToggle = useCallback(() => {
    const newActive = !isLuxPlusActive
    dispatch({ type: DEV_OVERRIDE_AUTH_ACCOUNT, overrides: { luxPlusActive: newActive, luxPlusRegion: currentRegion } })
    persistOverride({ luxPlusActive: newActive })
    if (newActive) {
      const stored = loadOverrides()
      if (stored.luxPlusJoinDate) {
        dispatch({ type: DEV_OVERRIDE_AUTH_ACCOUNT, overrides: { luxPlusJoinDate: stored.luxPlusJoinDate } })
      }
      if (stored.luxPlusSavings !== undefined) {
        dispatch({ type: DEV_OVERRIDE_AUTH_ACCOUNT, overrides: { luxPlusSavings: stored.luxPlusSavings } })
      }
    }
  }, [dispatch, isLuxPlusActive, currentRegion])

  const handleReset = useCallback(() => {
    isomorphicSessionStorage.remove(SESSION_KEY)
    window.location.reload()
  }, [])

  const tierRadios = useMemo(() => getTierOrder().map(tier => (
    <RadioLabel key={tier}>
      <RadioButton
        name="societe-tier"
        value={tier}
        size="small"
        checked={hasAccount && currentTier === tier}
        onChange={() => handleTierChange(tier)}
      />
      <BodyText variant="medium">{tier}</BodyText>
    </RadioLabel>
  )), [currentTier, hasAccount, handleTierChange])

  return <Pane padding={20}>
    <VerticalSpacer gap={16}>
      <Heading variant="heading5">Customer Profile</Heading>
      <BodyText variant="small" colour="neutral-three">
        Override user account properties for testing. Changes apply immediately to the main window.
      </BodyText>

      <Card>
        <VerticalSpacer gap={12}>
          <Heading variant="heading6">General</Heading>
          <FieldRow>
            <TextInput
              label="First Name"
              name="givenName"
              defaultValue={currentGivenName}
              key={`gn-${currentGivenName}`}
              onBlur={handleGivenNameBlur}
              onKeyDown={commitOnEnter}
            />
            <TextInput
              label="Last Name"
              name="surname"
              defaultValue={currentSurname}
              key={`sn-${currentSurname}`}
              onBlur={handleSurnameBlur}
              onKeyDown={commitOnEnter}
            />
          </FieldRow>
          <TextInput
            label="LE Credit"
            name="credits"
            type="number"
            min={0}
            step="0.01"
            defaultValue={currentCredits}
            key={`credits-${currentCredits}`}
            onBlur={handleCreditsBlur}
            onKeyDown={commitOnEnter}
          />
        </VerticalSpacer>
      </Card>

      <Card>
        <VerticalSpacer gap={12}>
          <CardHeader>
            <Heading variant="heading6">Société</Heading>
            <SwitchButton checked={hasAccount} onChange={handleSocieteToggle}>
              {hasAccount ? 'On' : 'Off'}
            </SwitchButton>
          </CardHeader>
          {hasAccount && <>
            <TierRow>
              {tierRadios}
            </TierRow>
            <FieldRow>
              <TextInput
                label="Points"
                name="points"
                type="number"
                min={0}
                defaultValue={currentPoints}
                key={`points-${currentPoints}`}
                onBlur={handlePointsBlur}
                onKeyDown={commitOnEnter}
              />
              <TextInput
                label="Status Credits"
                name="statusCredits"
                type="number"
                min={0}
                defaultValue={currentStatusCredits}
                key={`sc-${currentStatusCredits}`}
                onBlur={handleStatusCreditsBlur}
                onKeyDown={commitOnEnter}
              />
              <TextInput
                label="Nights"
                name="numberOfNights"
                type="number"
                min={0}
                defaultValue={currentNights}
                key={`nights-${currentNights}`}
                onBlur={handleNightsBlur}
                onKeyDown={commitOnEnter}
              />
            </FieldRow>
            <FieldRow>
              <TextInput
                label="Membership Start Date"
                name="societeStartDate"
                type="date"
                max={todayDateString()}
                defaultValue={currentMembershipStartDate}
                key={`ssd-${currentMembershipStartDate}`}
                onBlur={handleMembershipStartDateBlur}
                onKeyDown={commitOnEnter}
              />
              {currentStatusReviewDate && <div>
                <BodyText variant="small" colour="neutral-three">Review Date</BodyText>
                <BodyText variant="medium" weight="bold">
                  {formatDisplayDate(toDateInputValue(currentStatusReviewDate))}
                </BodyText>
              </div>}
            </FieldRow>
          </>}
        </VerticalSpacer>
      </Card>

      <Card>
        <VerticalSpacer gap={12}>
          <CardHeader>
            <Heading variant="heading6">LuxPlus+</Heading>
            <SwitchButton checked={isLuxPlusActive} onChange={handleLuxPlusToggle}>
              {isLuxPlusActive ? 'On' : 'Off'}
            </SwitchButton>
          </CardHeader>
          {isLuxPlusActive && <FieldRow>
            <TextInput
              label="Savings"
              name="luxPlusSavings"
              type="number"
              min={0}
              step="0.01"
              defaultValue={currentLuxPlusSavings}
              key={`lps-${currentLuxPlusSavings}`}
              onBlur={handleLuxPlusSavingsBlur}
              onKeyDown={commitOnEnter}
            />
            <TextInput
              label="Join Date"
              name="luxPlusJoinDate"
              type="date"
              max={todayDateString()}
              defaultValue={toDateInputValue(currentLuxPlusJoinDate)}
              key={`lpjd-${currentLuxPlusJoinDate}`}
              onBlur={handleLuxPlusJoinDateBlur}
              onKeyDown={commitOnEnter}
            />
          </FieldRow>}
        </VerticalSpacer>
      </Card>

      <BodyText variant="small" colour="neutral-three">
        Reset reloads the main window to restore real account data.
      </BodyText>
      <TextButton kind="secondary" onClick={handleReset}>
        Reset profile
      </TextButton>
    </VerticalSpacer>
  </Pane>
}

export default CustomerProfileTab
